import React from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

import clsx from 'clsx';

import {
  NoResults,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ClearIcon as Clear,
  Done,
  Edit,
  makeStyles,
  Stack,
} from '@gogame/components';

import NavigationItem from './NavigationItem';

const useStyles = makeStyles((theme) => ({
  editingItem: {
    border: `1px solid ${theme.palette.grey[300]}`,
    borderWidth: '0 1px 1px 0',
    height: '48px',
    paddingRight: '0',
    '&:first-child': {
      borderTopWidth: '1px',
    },
  },
  text: {
    letterSpacing: 0,
    fontSize: '13px',
  },
  dragging: {
    background: 'white',
    boxShadow: theme.shadows[4],
    '& ~ div': {
      borderTopWidth: '1px',
    },
  },
  noRes: {
    padding: '16px',
  },
  listItem: {
    padding: '10px 16px',
  },
}));

const EditableFavoriteList = ({ pages, onChange, className }) => {
  const classes = useStyles();
  const handleDelete = React.useCallback(
    (id) => () => {
      onChange(pages.filter((f) => f.id !== id));
    },
    [onChange, pages]
  );

  return (
    <List disablePadding className={className} component={'div'}>
      {pages.map((page) => {
        return (
          <ListItem
            dense
            component={'div'}
            size={'small'}
            className={classes.editingItem}
          >
            <ListItemText
              primary={page.title}
              classes={{
                primary: classes.text,
              }}
            />
            <IconButton
              size={'medium'}
              onClick={handleDelete(page.id)}
              tooltip={'Remove'}
            >
              <Clear />
            </IconButton>
          </ListItem>
        );
      })}
    </List>
  );
};

const StaticFavoriteList = ({ pages, onClose }) => {
  return (
    <List disablePadding>
      {pages.map((page) => {
        return <NavigationItem {...page} onClose={onClose} key={page.id} />;
      })}
    </List>
  );
};

const EDIT_MODE = 'edit';
const READ_MODE = 'read';

const Favorites = ({ pages, onChange, onClose }) => {
  const classes = useStyles();

  const [mode, setMode] = React.useState(READ_MODE);
  const [newFavoritePages, setNewFavoritePages] = React.useState(pages);

  const handleChangeMode = React.useCallback(async () => {
    if (newFavoritePages !== pages && mode === EDIT_MODE) {
      onChange(newFavoritePages);
    }
    setMode(mode === EDIT_MODE ? READ_MODE : EDIT_MODE);
  }, [newFavoritePages, pages, mode]);

  React.useEffect(() => {
    setNewFavoritePages(pages);
  }, [pages]);

  return (
    <Stack>
      <List disablePadding>
        <ListItem className={classes.listItem}>
          <ListItemText primary={'FAVORITES'} />
          {pages.length ? (
            <IconButton
              size={'medium'}
              WrapperProps={{
                style: { position: 'absolute', right: '0' },
              }}
              onClick={handleChangeMode}
              title={mode === EDIT_MODE ? 'Save changes' : 'Edit favorites'}
            >
              {mode === EDIT_MODE ? <Done /> : <Edit />}
            </IconButton>
          ) : null}
        </ListItem>
      </List>
      {pages && pages.length ? (
        <React.Fragment>
          {mode === READ_MODE ? (
            <StaticFavoriteList pages={pages} onClose={onClose} />
          ) : null}
          {mode === EDIT_MODE ? (
            <EditableFavoriteList
              pages={newFavoritePages}
              onChange={setNewFavoritePages}
            />
          ) : null}
        </React.Fragment>
      ) : (
        <div className={classes.noRes}>
          <NoResults inited={true} />
        </div>
      )}
    </Stack>
  );
};

export default Favorites;
